import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Card, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Loader2, Check } from "lucide-react";

interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  artistId: string;
  artistName: string;
}

export const InviteModal = ({ isOpen, onClose, artistId, artistName }: InviteModalProps) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [invitedOpportunities, setInvitedOpportunities] = useState<Set<string>>(new Set());

  const { data: opportunities, isLoading } = useQuery({
    queryKey: ['casting-opportunities'],
    queryFn: async () => {
      console.log('Fetching opportunities...');
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      console.log('Current user:', user.id);
      const { data, error } = await supabase
        .from('opportunities')
        .select(`
          *,
          profiles:created_by (
            full_name,
            avatar_url
          )
        `)
        .eq('created_by', user.id)
        .eq('status', 'published');

      if (error) {
        console.error('Error fetching opportunities:', error);
        throw error;
      }
      
      // Check existing invitations for each opportunity
      const { data: existingInvitations, error: invitationsError } = await supabase
        .from('opportunity_invitations')
        .select('opportunity_id')
        .eq('artist_id', artistId);

      if (invitationsError) {
        console.error('Error fetching existing invitations:', invitationsError);
      } else if (existingInvitations) {
        setInvitedOpportunities(new Set(existingInvitations.map(inv => inv.opportunity_id)));
      }
      
      console.log('Fetched opportunities:', data);
      return data;
    },
  });

  const handleInvite = async (opportunityId: string) => {
    setIsSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // First check if invitation exists
      const { data: existingInvitations, error: checkError } = await supabase
        .from('opportunity_invitations')
        .select('id')
        .eq('opportunity_id', opportunityId)
        .eq('artist_id', artistId);

      if (checkError) {
        console.error('Error checking existing invitation:', checkError);
        throw checkError;
      }

      if (existingInvitations && existingInvitations.length > 0) {
        toast({
          title: "Already invited",
          description: `${artistName} has already been invited to this opportunity`,
          variant: "destructive",
        });
        return;
      }

      const { error: insertError } = await supabase
        .from('opportunity_invitations')
        .insert({
          opportunity_id: opportunityId,
          artist_id: artistId,
          inviter_id: user.id,
        });

      if (insertError) throw insertError;

      setInvitedOpportunities(prev => new Set([...prev, opportunityId]));

      toast({
        title: "Invitation sent",
        description: `Successfully invited ${artistName} to the opportunity`,
      });
    } catch (error) {
      console.error('Error sending invitation:', error);
      toast({
        title: "Error",
        description: "Failed to send invitation. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Invite {artistName} to Opportunity</DialogTitle>
        </DialogHeader>
        
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : opportunities && opportunities.length > 0 ? (
          <div className="space-y-4 mt-4">
            {opportunities.map((opportunity) => (
              <Card key={opportunity.id} className="cursor-pointer hover:shadow-md transition-shadow">
                <CardHeader className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg">{opportunity.title}</CardTitle>
                      <CardDescription className="line-clamp-2">
                        {opportunity.description}
                      </CardDescription>
                    </div>
                    <Button
                      onClick={() => handleInvite(opportunity.id)}
                      disabled={invitedOpportunities.has(opportunity.id) || isSubmitting}
                      variant={invitedOpportunities.has(opportunity.id) ? "secondary" : "default"}
                      className="min-w-[80px]"
                    >
                      {invitedOpportunities.has(opportunity.id) ? (
                        <>
                          <Check className="h-4 w-4 mr-1" />
                          Invited
                        </>
                      ) : isSubmitting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Invite'
                      )}
                    </Button>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <p>No active opportunities found.</p>
            <Button 
              variant="link" 
              className="mt-2"
              onClick={() => window.location.href = '/opportunities'}
            >
              Create an opportunity
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};